import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Modal, Button } from "react-bootstrap";
import { toast } from "react-toastify";
import { BriefcaseBusiness, Building2, Link2, Plus, RefreshCw, Search, Trash2 } from "lucide-react";
import { useUser } from "./Context/UserContext.tsx";
import "./CSS/Management.css";

const API_BASE = "https://timetrakerapi.sntservices.in";
interface ClientRecord { id: number; name: string; pname: string; }

const Client: React.FC = () => {
  const { user } = useUser();
  const adminId = user?.id;
  const [clients, setClients] = useState<ClientRecord[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [newName, setNewName] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [error, setError] = useState("");

  const loadClients = useCallback(async (signal?: AbortSignal) => {
    if (!adminId) return;
    try {
      const response = await axios.get<ClientRecord[]>(`${API_BASE}/GetClient`, { params: { Adminid: adminId }, signal });
      if (!signal?.aborted) setClients(Array.isArray(response.data) ? response.data : []);
      setError("");
    } catch (err) {
      if (axios.isCancel(err)) return;
      if (axios.isAxiosError(err) && err.response?.status === 404) { setClients([]); setError(""); return; }
      setError("Clients could not be loaded. Try refreshing the page.");
    }
  }, [adminId]);

  useEffect(() => {
    if (!adminId) { setLoading(false); return; }
    const controller = new AbortController();
    setLoading(true);
    loadClients(controller.signal).finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [adminId, loadClients]);

  const selected = clients.find((client) => client.id === selectedId);
  const filtered = useMemo(() => clients.filter((client) =>
    `${client.name} ${client.pname || ""}`.toLowerCase().includes(search.trim().toLowerCase())
  ), [clients, search]);
  const linkedCount = clients.filter((client) => !!client.pname).length;

  const addClient = async (event: React.FormEvent) => {
    event.preventDefault();
    const name = newName.trim();
    if (!name) { setError("Enter a client name."); return; }
    setSaving(true);
    setError("");
    try {
      await axios.post(`${API_BASE}/AddClient`, null, { params: { Adminid: adminId, name } });
      setNewName("");
      await loadClients();
      toast.success("Client added successfully");
    } catch {
      setError("Client could not be added. Please try again.");
    } finally { setSaving(false); }
  };

  const deleteClient = async () => {
    if (!selected) return;
    setDeleting(true);
    setError("");
    try {
      await axios.delete(`${API_BASE}/deleteClient`, { params: { adminid: adminId, clientid: selected.id } });
      setSelectedId(null);
      setConfirmDelete(false);
      await loadClients();
      toast.success("Client deleted successfully");
    } catch {
      setError("Client could not be deleted. Check whether it is still used by a project.");
    } finally { setDeleting(false); }
  };

  return <main className="management-page">
    <header className="management-header">
      <div><span className="management-eyebrow">Workspace / Clients</span><h1>Clients</h1><p>Keep client records and their project links easy to find.</p></div>
      <div className="management-header-actions"><button className="management-button" type="button" onClick={() => loadClients()}><RefreshCw size={15} /> Refresh</button><Link className="management-button management-button-primary" to="/admin-dashboard/project"><BriefcaseBusiness size={15} /> View projects</Link></div>
    </header>
    {error && <div className="management-alert" role="alert">{error} <button type="button" onClick={() => setError("")}>Dismiss</button></div>}
    <section className="management-metrics" aria-label="Client overview">
      <div className="management-metric"><span><Building2 size={16} /> Total clients</span><strong>{clients.length}</strong></div>
      <div className="management-metric"><span><Link2 size={16} /> With a project</span><strong>{linkedCount}</strong></div>
      <div className="management-metric"><span><BriefcaseBusiness size={16} /> Awaiting a project</span><strong>{clients.length - linkedCount}</strong></div>
    </section>
    <div className="management-layout">
      <section className="management-panel">
        <div className="management-panel-heading"><div><h2>Client directory</h2><small>{clients.length} clients</small></div></div>
        <form className="management-create" onSubmit={addClient}><input className="management-input" aria-label="New client name" placeholder="New client name" value={newName} onChange={(event) => setNewName(event.target.value)} /><button className="management-button management-button-primary" disabled={saving || !newName.trim()}><Plus size={15} /> {saving ? "Adding…" : "Add"}</button></form>
        <label className="management-search"><Search size={15} /><input className="management-input" aria-label="Search clients" placeholder="Search clients or projects" value={search} onChange={(event) => setSearch(event.target.value)} /></label>
        <div className="management-list">{loading ? <p className="management-list-empty">Loading clients…</p> : filtered.length ? filtered.map((client) => <button type="button" key={client.id} className={`management-list-item${selectedId === client.id ? " is-active" : ""}`} onClick={() => setSelectedId(client.id)}><span className="management-list-icon">{client.name?.trim().charAt(0).toUpperCase() || "C"}</span><span className="management-list-copy"><strong>{client.name}</strong><small>{client.pname || "No project linked"}</small></span></button>) : <p className="management-list-empty">{search ? "No clients match your search." : "No clients yet. Add your first client above."}</p>}</div>
      </section>
      <section className="management-panel management-detail">
        {selected ? <><div className="management-detail-header"><div><span className="management-eyebrow">Client details</span><h2>{selected.name}</h2><p>Client ID #{selected.id}</p></div><button className="management-button management-button-danger" type="button" onClick={() => setConfirmDelete(true)}><Trash2 size={15} /> Delete client</button></div><div className="management-detail-body"><h3 className="management-section-title">Project relationship</h3><p className="management-section-copy">The project currently linked to this client.</p>{selected.pname ? <div className="management-info-card"><BriefcaseBusiness size={21} /><div><strong>{selected.pname}</strong><small>Linked project</small></div></div> : <div className="management-info-card"><Link2 size={21} /><div><strong>No project linked yet</strong><small>Assign a client from the Project page.</small></div></div>}<div style={{ marginTop: 20 }}><Link className="management-button" to="/admin-dashboard/project"><BriefcaseBusiness size={15} /> Manage projects</Link></div></div></> : <div className="management-detail-empty"><Building2 size={36} /><h2>Select a client</h2><p>Choose a client from the directory to see its project relationship and actions.</p></div>}
      </section>
    </div>
    <Modal show={confirmDelete} onHide={() => setConfirmDelete(false)} centered><Modal.Header closeButton><Modal.Title>Delete client?</Modal.Title></Modal.Header><Modal.Body>This will delete {selected?.name}. This action cannot be undone.</Modal.Body><Modal.Footer><Button variant="secondary" onClick={() => setConfirmDelete(false)}>Cancel</Button><Button variant="danger" disabled={deleting} onClick={deleteClient}>{deleting ? "Deleting…" : "Delete client"}</Button></Modal.Footer></Modal>
  </main>;
};

export default Client;
